<!-- eslint-disable -->
<template>
  <div class="card border rounded-4 shadow-sm h-100 w-100">
    <div class="card-body p-3 p-lg-4 d-flex flex-column gap-4 h-100">
      <div class="d-flex flex-column gap-1">
        <h2 class="fw-bold text-x mb-0">Section Access for Selected Reviewer</h2>
        <p class="text-secondary text-xs mb-0">
          Define permissions for <strong>{{ currentReviewerScopeLabel }}</strong> on <strong>{{ currentFormLabel }}</strong> while in <strong>{{ currentStatusLabel }}</strong> status.
        </p>
      </div>

      <div v-if="contextLocked" class="alert alert-info border-0 rounded-4 p-3 mb-0 section-access-architecture-note">
        <p class="fw-bold text-xs mb-2">Current Permission Context</p>
        <div class="row g-3">
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Workflow</p>
            <p class="fw-bold text-xs mb-0">{{ workflowName }}</p>
          </div>
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Form</p>
            <p class="fw-bold text-xs mb-0">{{ currentFormLabel }}</p>
          </div>
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Transition</p>
            <p class="fw-bold text-xs mb-0">{{ transitionLabel }}</p>
          </div>
          <div class="col-12 col-md-3">
            <p class="text-secondary text-xxs text-uppercase fw-bold mb-1">Reviewer</p>
            <p class="fw-bold text-xs mb-0">{{ currentReviewerScopeLabel }}</p>
          </div>
        </div>
      </div>

      <div class="alert alert-info border-0 rounded-4 p-3 d-flex align-items-start gap-3 mb-0 section-access-architecture-note">
        <i class="bi bi-info-circle-fill mt-1"></i>
        <div class="text-xxs">
          <p class="fw-bold mb-1">Architecture Note</p>
          <p class="mb-0 opacity-75">
            <span v-if="reviewerRequired">These permissions only apply to this specific reviewer, for this specific form, while the form is in this specific status. Permissions are not global.</span>
            <span v-else>{{ optionalHelperText }}</span>
          </p>
        </div>
      </div>

      <SectionAccessContextSelector
        :form-options="formOptions"
        :status-options="statusOptions"
        :reviewer-options="reviewerOptions"
        :selected-form-id="selectedFormId"
        :selected-status-id="selectedStatusId"
        :selected-reviewer-assignment-key="selectedReviewerAssignmentKey"
        :current-form-label="currentFormLabel"
        :current-status-label="currentStatusLabel"
        :current-reviewer-scope-label="currentReviewerScopeLabel"
        :context-sentence="contextSentence"
        @update:selectedFormId="$emit('update:selectedFormId', $event)"
        @update:selectedStatusId="$emit('update:selectedStatusId', $event)"
        @update:selectedReviewerAssignmentKey="$emit('update:selectedReviewerAssignmentKey', $event)"
      />

      <div v-if="sectionAccessLoading" class="text-secondary text-xs px-2 py-5 text-center">
        <div class="spinner-border spinner-border-sm me-2 text-primary" role="status"></div>
        Loading permissions matrix for the selected scope...
      </div>

      <div v-else-if="!selectedFormId || !selectedStatusId" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <div class="entity-type-icon section-access-state-icon bg-light text-secondary d-inline-flex align-items-center justify-content-center mb-3 mx-auto shadow-sm">
            <i class="bi bi-layers"></i>
          </div>
          <p class="fw-bold text-xs mb-1">Select form and status first</p>
          <p class="text-secondary text-xxs mb-0 mx-auto section-access-state-message">
            Section access configuration depends on the selected form and status. Choose them above to continue.
          </p>
        </div>
      </div>

      <div v-else-if="!reviewerOptions.length" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <div class="entity-type-icon section-access-state-icon d-inline-flex align-items-center justify-content-center mb-3 mx-auto shadow-sm" :class="reviewerRequired ? 'bg-warning-subtle text-warning' : 'bg-primary-subtle text-primary'">
            <i :class="reviewerRequired ? 'bi bi-person-x' : 'bi bi-info-circle'"></i>
          </div>
          <p class="fw-bold text-xs mb-1">{{ reviewerRequired ? 'Assign reviewers before configuring access' : 'Section access is optional' }}</p>
          <p class="text-secondary text-xxs mb-0 mx-auto section-access-state-message">
            {{ reviewerRequired ? 'No reviewers found for this status. Go to the Reviewers tab to assign roles or users first.' : optionalHelperText }}
          </p>
        </div>
      </div>

      <div v-else-if="!workflowActions.length" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <div class="entity-type-icon section-access-state-icon bg-danger-subtle text-danger d-inline-flex align-items-center justify-content-center mb-3 mx-auto shadow-sm">
            <i class="bi bi-lightning-charge"></i>
          </div>
          <p class="fw-bold text-xs mb-1">No reusable actions available</p>
          <p class="text-secondary text-xxs mb-0 mx-auto section-access-state-message">
            You must create actions in the Action Library before you can build a permissions matrix.
          </p>
        </div>
      </div>

      <div v-else-if="!selectedReviewerAssignmentKey" class="card border rounded-4 bg-white bg-opacity-75">
        <div class="card-body py-5 px-3 text-center">
          <div class="entity-type-icon section-access-state-icon bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center mb-3 mx-auto shadow-sm">
            <i class="bi bi-cursor"></i>
          </div>
          <p class="fw-bold text-xs mb-1">Select a reviewer to configure permissions</p>
          <p class="text-secondary text-xxs mb-0 mx-auto section-access-state-message">
            Choose which role or user reviewer you are currently assigning form permissions to.
          </p>
        </div>
      </div>

      <template v-else>
        <SectionAccessMatrix
          :rows="matrixRows"
          :actions="workflowActions"
          :section-access-state="sectionAccessState"
          :bulk-action-ids="bulkActionIds"
          :disabled="matrixDisabled"
          :reviewer-label="currentReviewerScopeLabel"
          @toggle-section-action="$emit('toggle-section-action', $event)"
          @toggle-bulk-action="$emit('toggle-bulk-action', $event)"
          @apply-bulk-access="$emit('apply-bulk-access')"
          @clear-all-access="$emit('clear-all-access')"
        />

        <div class="d-flex justify-content-end border-top pt-4 mt-2">
          <button
            type="button"
            class="btn btn-primary-dark text-xs rounded-3 px-5 py-2 shadow-sm"
            :disabled="saveDisabled || sectionAccessSaving"
            @click="$emit('save-section-access')"
          >
            <i class="bi bi-shield-check me-2"></i>{{ sectionAccessSaving ? 'Saving Matrix...' : 'Save Section Permissions' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';

const section_access_tab_components = {
  SectionAccessContextSelector: defineAsyncComponent(() => import('./SectionAccessContextSelector.vue')),
  SectionAccessMatrix: defineAsyncComponent(() => import('./SectionAccessMatrix.vue')),
};

export default {
  name: 'SectionAccessTab',
  components: section_access_tab_components,
  props: {
    formOptions: {
      type: Array,
      default: () => [],
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
    reviewerOptions: {
      type: Array,
      default: () => [],
    },
    selectedFormId: {
      type: String,
      default: '',
    },
    selectedStatusId: {
      type: String,
      default: '',
    },
    selectedReviewerAssignmentKey: {
      type: String,
      default: '',
    },
    currentFormLabel: {
      type: String,
      default: 'No form selected',
    },
    currentStatusLabel: {
      type: String,
      default: 'No status selected',
    },
    currentReviewerScopeLabel: {
      type: String,
      default: 'No reviewer selected',
    },
    contextSentence: {
      type: String,
      default: '',
    },
    sectionAccessLoading: {
      type: Boolean,
      default: false,
    },
    sectionAccessSaving: {
      type: Boolean,
      default: false,
    },
    workflowActions: {
      type: Array,
      default: () => [],
    },
    matrixRows: {
      type: Array,
      default: () => [],
    },
    sectionAccessState: {
      type: Object,
      default: () => ({}),
    },
    bulkActionIds: {
      type: Array,
      default: () => [],
    },
    saveDisabled: {
      type: Boolean,
      default: true,
    },
    matrixDisabled: {
      type: Boolean,
      default: false,
    },
    reviewerRequired: {
      type: Boolean,
      default: false,
    },
    automaticOnlyStatus: {
      type: Boolean,
      default: false,
    },
    optionalHelperText: {
      type: String,
      default: 'Section access is optional because this status uses automatic transitions.',
    },
    workflowName: {
      type: String,
      default: 'Workflow',
    },
    transitionLabel: {
      type: String,
      default: 'No transition selected',
    },
    contextLocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:selectedFormId',
    'update:selectedStatusId',
    'update:selectedReviewerAssignmentKey',
    'toggle-section-action',
    'toggle-bulk-action',
    'apply-bulk-access',
    'clear-all-access',
    'save-section-access',
  ],
};
</script>

<style scoped>
.section-access-architecture-note {
  background-color: rgb(32 16 63 / 5%);
  color: #20103f;
}

.section-access-state-icon {
  width: 48px;
  height: 48px;
}

.section-access-state-message {
  max-width: 320px;
}
</style>
